import { useCallback, useEffect, useState } from 'react'

import type { WizardFarm } from '../../types/farmWizard'
import { ensureFarmList } from '../../utils/farmWizard'
import { getActiveTenantKey, getTenantScopedKey } from '../../offline/tenant'
import { recordCrewEvent } from './crewModeTelemetry'

// Crew Mode's own farm context -- deliberately independent of the admin
// app's activeContext selection (src/utils/activeContextSelection.ts).
// Charter D-1: scoping is explicit at capture time, not inherited as a UI
// convention -- before this hook, the launcher resolved the active farm
// one way and each of the four capture screens resolved it another
// (list[0], unconditionally), which is exactly what let the launcher show
// a stream as available while the capture screen itself silently picked a
// different farm and rejected every write. One shared hook now backs the
// launcher and all four capture screens so they can't disagree again.
//
// Deliberately does NOT filter farms down to ones with an enabled stream:
// the strategy doc's own flow (§9, "Granja si aplica más de una") shows
// every farm the operator has access to -- there is no per-operator farm
// assignment anywhere server-side, an operator PIN is tenant-wide. Picking
// a farm with nothing enabled already surfaces the existing, correct
// crewMode.home.noStreams message; filtering here would just be unrequested
// complexity for a case that's already handled.

const LAST_SELECTED_FARM_BASE_KEY = 'crewModeLastSelectedFarm'

const readRememberedFarmId = (tenantKey: string): string => {
  try {
    return globalThis.window?.localStorage?.getItem(getTenantScopedKey(LAST_SELECTED_FARM_BASE_KEY, tenantKey)) ?? ''
  } catch {
    return ''
  }
}

const writeRememberedFarmId = (tenantKey: string, farmId: string): void => {
  try {
    globalThis.window?.localStorage?.setItem(getTenantScopedKey(LAST_SELECTED_FARM_BASE_KEY, tenantKey), farmId)
  } catch {
    // ignore
  }
}

export type UseCrewModeFarmResult = {
  farms: WizardFarm[]
  farmId: string
  /** Persists the choice (remembered next time, per Charter O-5's offline-first spirit -- no network round-trip) and records a farm.switch telemetry event, mirroring OperatorGate's operator.switch. */
  selectFarm: (nextFarmId: string) => void
  /** True until the first farm-list load resolves -- mirrors the old per-screen `farmsLoaded` gate so dependent effects (telemetry, today-list) don't fire on an empty list. */
  isLoading: boolean
}

export const useCrewModeFarm = (): UseCrewModeFarmResult => {
  const [farms, setFarms] = useState<WizardFarm[]>([])
  const [farmId, setFarmIdState] = useState('')
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    let cancelled = false
    void ensureFarmList().then((list) => {
      if (cancelled) return
      setFarms(list)
      setIsLoading(false)
      setFarmIdState((current) => {
        if (current && list.some((farm) => String(farm.id) === current)) return current
        const tenantKey = getActiveTenantKey()
        const remembered = readRememberedFarmId(tenantKey)
        if (remembered && list.some((farm) => String(farm.id) === remembered)) return remembered
        return list.length > 0 ? String(list[0].id) : ''
      })
    })
    return () => {
      cancelled = true
    }
  }, [])

  const selectFarm = useCallback((nextFarmId: string) => {
    setFarmIdState(nextFarmId)
    writeRememberedFarmId(getActiveTenantKey(), nextFarmId)
    recordCrewEvent('farm.switch', { farmId: nextFarmId })
  }, [])

  return { farms, farmId, selectFarm, isLoading }
}
